import { Dialog } from 'primereact/dialog';
import React, { useEffect, useMemo, useState } from 'react';
import { useEditLeadMutation, useGetLeadsStatusesQuery } from '../../../api/leads.js';
import BaseButton from '../../form/BaseButton.jsx';
import IconCloseFull from '../../images/IconCloseFull.jsx';
import { useErrorToast } from '../../../hooks/useToast.jsx';
import BaseDropdown from '../../form/BaseDropdown.jsx';

const LeadStatusChangeModal = React.memo(({ changeStatusVisible, setChangeStatusVisible, lead }) => {
    const [editLead, { isLoading: isChanging }] = useEditLeadMutation();

    const { data: statusesData, isLoading: statusesLoading } = useGetLeadsStatusesQuery();

    const [selectedStatus, setSelectedStatus] = useState(lead.status);

    useEffect(() => {
        setSelectedStatus(lead.status);
    }, [lead.status]);

    const statusOptions = useMemo(() => {
        return Array.isArray(statusesData?.response?.lead_statuses)
            ? statusesData.response.lead_statuses.map((statusItem) => ({
                  label: statusItem.label,
                  value: statusItem.value
              }))
            : [];
    }, [statusesData]);

    const hideDialog = () => {
        setChangeStatusVisible(false);
    };

    const confirmStatusChange = async () => {
        try {
            await editLead({ id: lead.id, data: { status: selectedStatus } }).then((apiResponse) => {
                if (!apiResponse.data?.success) {
                    useErrorToast(apiResponse.data?.message);
                }
            });
        } catch (error) {
            console.error('Failed to change the lead status:', error);
            useErrorToast('An unexpected error occurred while changing the status.');
        }

        setChangeStatusVisible(false);
    };

    if (!changeStatusVisible) return null;

    return (
        <Dialog
            className='rounded-4xl'
            visible={changeStatusVisible}
            style={{ width: '400px' }}
            breakpoints={{ '420px': '320px' }}
            onHide={hideDialog}
            content={() => (
                <div className='flex flex-col justify-center items-center px-6 py-8 pt-12 bg-white rounded-4xl'>
                    <button onClick={hideDialog} className='absolute top-6 right-6'>
                        <IconCloseFull className='fill-theme1-text5' />
                    </button>
                    <div className='text-theme1-primary text-[22px] mb-[30px] text-center leading-tight'>
                        Select a different status for this lead
                    </div>
                    <BaseDropdown
                        name='status'
                        placeholder='Select Status'
                        value={selectedStatus}
                        setValue={setSelectedStatus}
                        options={statusOptions}
                        disabled={statusesLoading}
                    />
                    <div className='flex justify-between gap-4 mt-8 w-full'>
                        <BaseButton
                            className='btn-red-outline w-[47%] flex-shrink-0'
                            textValue='Cancel'
                            onClick={hideDialog}
                        />

                        <BaseButton
                            className={`bg-theme1-primary text-white w-[47%] flex-shrink-0 ${isChanging ? 'bg-theme1-text3' : ''}`}
                            textValue={isChanging ? 'Changing...' : 'Confirm'}
                            onClick={confirmStatusChange}
                            disabled={isChanging}
                        />
                    </div>
                </div>
            )}
        ></Dialog>
    );
});

LeadStatusChangeModal.displayName = 'LeadStatusChangeModal';

export default LeadStatusChangeModal;
